import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { useContext, useEffect } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ProviderCredentialingDocuments, ProviderService } from '../../../../js/api';
import { DocumentArrayField } from '../../../../js/app/components/DocumentArrayField';
import { FileUploader } from '../../../../js/app/components/FileUploader';
import { FormFeedback } from '../../../../js/app/components/FormFeedback';
import { FormSubmitContext } from '../../../../js/app/contexts/FormSubmitContext';
import { useDocumentTitle } from '../../../../js/app/hooks/use-document-title';
import { providerQueryKeys } from '../../../../js/app/query-keys-factories/providers';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../js/utils/forms';
import { Button } from '../../components/button';

const labelClassName = 'tw:text-brand-green-0 tw:font-bold tw:mb-1 tw:block';

const DocumentField = ({
  id,
  label = startCase(toLower(id)),
  control,
  readonly = false,
  clearable = true,
  errors,
}: {
  id: string;
  label?: string;
  control: React.ComponentProps<typeof FileUploader>['control'];
  readonly?: boolean;
  clearable?: boolean;
  errors?: FieldErrors;
}) => {
  return (
    <div className="tw:mb-4">
      <label htmlFor={id} className={labelClassName}>
        {label}
      </label>
      <FileUploader
        name={id}
        className="mb-3"
        control={control}
        dest="providers_documents"
        required={false}
        readOnly={readonly}
        isClearable={clearable}
      />
      <FormFeedback clientError={errors?.[id]} />
    </div>
  );
};

export const DocumentsPage = () => {
  useDocumentTitle('Documents');
  const { isSubmitEnabled } = useContext(FormSubmitContext);
  const {
    clearErrors,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm();

  const { data: documents } = useQuery({
    queryKey: providerQueryKeys.detailCredentialingDocuments(),
    queryFn: () => ProviderService.providerCurrentCredentialingDocumentsRetrieve(),
  });

  const queryClient = useQueryClient();
  const mutationProviderCurrentCredentialingDocumentsUpdate = useMutation({
    mutationFn: async (data: ProviderCredentialingDocuments) => {
      await ProviderService.providerCurrentCredentialingDocumentsUpdate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(providerQueryKeys.all());
    },
  });

  const onSubmit = async (data) => {
    try {
      await mutationProviderCurrentCredentialingDocumentsUpdate.mutateAsync(data);
      toast.success('Documents updated successfully.');
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
      handleApiError(errors);
    }
  };

  const handleSubmitClick = () => {
    clearErrors();
  };

  useEffect(() => {
    reset({
      ...documents,
    });
  }, [documents, reset]);

  return (
    <div className="tw:mt-4 tw:max-w-4xl tw:mx-auto tw:mb-12 tw:px-4">
      <div className="tw:bg-white tw:rounded-lg tw:shadow tw:p-6">
        <div className="tw:text-brand-green-0 tw:border-b tw:border-zinc-300 tw:mb-4">
          <h1 className="tw:!text-3xl tw:font-bold tw:mb-2">Documents</h1>
          <p className="tw:mb-2 tw:text-sm tw:text-zinc-700">
            On this page, you can find your most recent contract with Sendit and NPDB report if we
            have it.
          </p>
          <p className="tw:mb-0 tw:text-sm tw:text-zinc-700">
            Any documents added under Private Documents are only available to you and Sendit and are
            <strong>&nbsp;NOT</strong> accessible by Healthcare organizations
          </p>
        </div>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {errors?.root?.non_field_errors && (
            <div className="tw:mb-4 tw:rounded-md tw:border tw:border-stoplight-danger tw:bg-stoplight-danger/10 tw:px-4 tw:py-3 tw:text-sm tw:text-stoplight-danger">
              {errors.root.non_field_errors.message}
            </div>
          )}
          <DocumentField
            id="contract"
            control={control}
            label="Independent Contractor Professional Services Agreement"
            readonly={true}
            clearable={false}
            errors={errors}
          />
          <DocumentField
            id="npdb_report"
            label="NPDB"
            control={control}
            clearable={false}
            errors={errors}
          />
          <DocumentArrayField
            className="mt-3"
            labelClassName={labelClassName}
            id="misc_files"
            label="Private Documents"
            isRequired={false}
            control={control}
            errors={errors}
          />
          <div className="tw:flex tw:justify-end tw:mt-4">
            <Button
              color="brandgreen0"
              type="submit"
              onClick={() => handleSubmitClick()}
              disabled={
                mutationProviderCurrentCredentialingDocumentsUpdate.isLoading || !isSubmitEnabled
              }
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
